// `/admin/retire`'s pure half: grouping the week's candidates by owner, and the sentences the page says.
// Node-runnable (`retire.test.ts`), so only `import type` here.

import type { RetireAnswer, RetireCandidate } from '../api/types';

export interface OwnerGroup {
  key: string;
  label: string;
  rows: RetireCandidate[];
}

// "omair.khan@cartesia.ai" → "Omair"; a bank account rotting in the auto lane's queue → "Auto queue".
export function ownerLabel(candidate: RetireCandidate): string {
  if (candidate.origin === 'auto_queue') return 'Auto queue';
  const localPart = (candidate.owner_email || '').split('@')[0] ?? '';
  const firstName = localPart.split(/[._]/)[0] ?? '';
  if (!firstName) return candidate.owner_email || 'Unknown';
  return firstName.charAt(0).toUpperCase() + firstName.slice(1);
}

function groupKey(candidate: RetireCandidate): string {
  return candidate.origin === 'auto_queue' ? 'auto_queue' : candidate.owner_email;
}

// One group per owner, biggest first, then by name; rows keep the proposal's order (coldest first).
export function groupByOwner(candidates: RetireCandidate[]): OwnerGroup[] {
  const groups = new Map<string, OwnerGroup>();
  for (const candidate of candidates) {
    const key = groupKey(candidate);
    const group = groups.get(key) ?? { key, label: ownerLabel(candidate), rows: [] };
    group.rows.push(candidate);
    groups.set(key, group);
  }
  return [...groups.values()].sort((left, right) =>
    right.rows.length - left.rows.length || left.label.localeCompare(right.label));
}

export function accountsWord(count: number): string {
  return count === 1 ? '1 account' : `${count} accounts`;
}

// The button: "Retire 39", or why it cannot retire anything while SCOUT_RETIRE is off.
export const RETIRE_OFF_LABEL = 'Retire is off (SCOUT_RETIRE)';

export function retireButtonLabel(tickedCount: number, retireOn: boolean): string {
  return retireOn ? `Retire ${tickedCount}` : RETIRE_OFF_LABEL;
}

// What an approval did, in one line.
export function answerLine(answer: RetireAnswer): string {
  if (answer.outcome === 'applied' || answer.outcome === 'failed') {
    return `Retired ${answer.retired_count} · skipped ${answer.skipped_count} · failed ${answer.failed_count}`;
  }
  if (answer.outcome === 'dry_run') {
    return `Dry run: ${answer.dry_run_count} would be retired (the Salesforce write guard is off). The week is still waiting.`;
  }
  return answer.message || answer.outcome;
}
